import { ActionContext } from "vuex";
import { Module, VuexModule, getModule, Mutation } from "vuex-module-decorators";
import {
	DBEntity,
	DBType,
	IModel,
	store,
	VIPCapabilityCertificateDB,
	VIPCapabilityDB,
	VIPCapabilityEventDB,
	VIPCapabilityRequestDB,
	VIPCapabilityRequestEventDB,
	VIPCertificateDB,
	VIPCertificateEventDB,
	VIPCertificateIpDB,
	VIPCompanyDB,
	VIPContactDB,
	VIPDocumentDB,
	VIPEventDB,
	VIPIpDB,
	VIPIpEquivalenceDB,
	VIPProductionDB,
	VIPProjectDB,
	VIPProjectIpDB,
	VIPUserDB,
} from "@/loader";

export interface InsertOrUpdatePayload<I, T extends IModel<I>> {
	type: DBType;
	item: I | T;
	result?: T;
}

export interface InsertOrUpdateManyPayload<I, T extends IModel<I>> {
	type: DBType;
	items: (I | T)[];
	result?: T[];
}

@Module({
	name: "db",
	store,
	dynamic: true,
	namespaced: true,
})
export class DBModule extends VuexModule {
	public capabilities: VIPCapabilityDB = new VIPCapabilityDB();
	public capabilityCertificates: VIPCapabilityCertificateDB = new VIPCapabilityCertificateDB();
	public capabilityEvents: VIPCapabilityEventDB = new VIPCapabilityEventDB();
	public capabilityRequests: VIPCapabilityRequestDB = new VIPCapabilityRequestDB();
	public capabilityRequestEvents: VIPCapabilityRequestEventDB = new VIPCapabilityRequestEventDB();
	public certificateEvents: VIPCertificateEventDB = new VIPCertificateEventDB();
	public certificateIPS: VIPCertificateIpDB = new VIPCertificateIpDB();
	public certificates: VIPCertificateDB = new VIPCertificateDB();
	public companies: VIPCompanyDB = new VIPCompanyDB();
	public contacts: VIPContactDB = new VIPContactDB();
	public documents: VIPDocumentDB = new VIPDocumentDB();
	public events: VIPEventDB = new VIPEventDB();
	public ipEquivalences: VIPIpEquivalenceDB = new VIPIpEquivalenceDB();
	public ips: VIPIpDB = new VIPIpDB();
	public productions: VIPProductionDB = new VIPProductionDB();
	public projectIps: VIPProjectIpDB = new VIPProjectIpDB();
	public projects: VIPProjectDB = new VIPProjectDB();
	public users: VIPUserDB = new VIPUserDB();

	@Mutation
	public clear(payload: { type: DBType }) {
		if (this.hasOwnProperty(payload.type)) {
			(<DBEntity<any, any>>this[payload.type]).clear();
		}
	}

	@Mutation
	public insert<I, T extends IModel<I>>(payload: InsertOrUpdatePayload<I, T>) {
		if (this.hasOwnProperty(payload.type)) {
			payload.result = (<DBEntity<any, any>>this[payload.type]).insert(payload.item);
		}
	}

	@Mutation
	public insertOrUpdate<I, T extends IModel<I>>(payload: InsertOrUpdatePayload<I, T>) {
		if (this.hasOwnProperty(payload.type)) {
			payload.result = (<DBEntity<any, any>>this[payload.type]).insertOrUpdate(payload.item);
		}
	}

	@Mutation
	public insertMany<I, T extends IModel<I>>(payload: InsertOrUpdateManyPayload<I, T>) {
		if (this.hasOwnProperty(payload.type)) {
			payload.result = (<DBEntity<any, any>>this[payload.type]).insertMany(payload.items);
		}
	}

	@Mutation
	public insertOrUpdateMany<I, T extends IModel<I>>(payload: InsertOrUpdateManyPayload<I, T>) {
		if (this.hasOwnProperty(payload.type)) {
			payload.result = (<DBEntity<any, any>>this[payload.type]).insertOrUpdateMany(payload.items);
		}
	}

	@Mutation
	public remove(payload: { type: DBType, item: any, force?: boolean }) {
		if (this.hasOwnProperty(payload.type)) {
			(<DBEntity<any, any>>this[payload.type]).remove(payload.item, payload.force);
		}
	}

	@Mutation
	public removeMany(payload: { type: DBType, items: any[], force?: boolean }) {
		if (this.hasOwnProperty(payload.type)) {
			(<DBEntity<any, any>>this[payload.type]).removeMany(payload.items, payload.force);
		}
	}
}

export const db = getModule(DBModule);

export function commitDBClear(context: ActionContext<any, any>, payload: { type: DBType }) {
	context.commit("db/clear", payload, { root: true });
}

export function commitDBInsert<I, T extends IModel<I>>(context: ActionContext<any, any>, payload: InsertOrUpdatePayload<I, T>) {
	context.commit("db/insert", payload, { root: true });
}

export function commitDBInsertOrUpdate<I, T extends IModel<I>>(context: ActionContext<any, any>, payload: InsertOrUpdatePayload<I, T>) {
	context.commit("db/insertOrUpdate", payload, { root: true });
}

export function commitDBInsertMany<I, T extends IModel<I>>(context: ActionContext<any, any>, payload: InsertOrUpdateManyPayload<I, T>) {
	context.commit("db/insertMany", payload, { root: true });
}

export function commitDBInsertOrUpdateMany<I, T extends IModel<I>>(context: ActionContext<any, any>, payload: InsertOrUpdateManyPayload<I, T>) {
	context.commit("db/insertOrUpdateMany", payload, { root: true });
}

export function commitDBRemove(context: ActionContext<any, any>, payload: { type: DBType, item: any, force?: boolean }) {
	context.commit("db/remove", payload, { root: true });
}

export function commitDBRemoveMany(context: ActionContext<any, any>, payload: { type: DBType, items: any[], force?: boolean }) {
	context.commit("db/removeMany", payload, { root: true });
}
